"use client";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import icon from "../../public/ai-logo.png";
import ai from "../../public/ai-icon.png";
import smily from "../../public/emoji-pink.png";
import msg from "../../public/send-msg.png";
import { Move } from "lucide-react";
import down from "../../public/down-icon.png";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/lib/redux/store";
import {
  getChatHistory,
  getUserByIp,
  GuestFormDataPayload,
  handleChatBot,
  sendChatBotFormData,
} from "@/lib/redux/slice/chatbotSlice";
import Avatar from "../../public/bot-avatar-icon-chat.png";
import { useRouter } from "next/navigation";
import link from "../../public/pvt-space-two.png";
import { getIPAddress } from "@/lib/redux/slice/authSlice";
import ChatBotLoader from "./ChatBotLoader";
import {
  ChatBotPropertyDetails,
  Property,
} from "@/interfaces/propertyinterface";
import { formatPropertyName } from "@/lib/utils";
import { FieldValues, SubmitHandler, useForm } from "react-hook-form";
import {
  guestFormValidation,
  GuestFormValidationType,
} from "@/validations/guestFormSchema";
import { yupResolver } from "@hookform/resolvers/yup";

interface EmojiObject {
  native: string;
}

interface ChatBot {
  auth: string | null;
  question: string;
  IP: string | null;
}

export interface ChatHistory {
  IP: string | null;
  email: string | null;
}

interface ChatMessage {
  sender: string | "bot";
  message: string;
  properties: ChatBotPropertyDetails[];
  timestamp?: string;
}

interface ChatItem {
  req: string;
  res: {
    properties: Property[];
    response: string;
  };
  timestamp: string;
}

interface GuestFormData {
  name: string;
  email: string;
  mobile: string;
}

export default function ChatBox() {
  const dispatch = useDispatch<AppDispatch>();
  const {
    register,
    reset,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<GuestFormValidationType>({
    resolver: yupResolver(guestFormValidation),
  });
  const scrollRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const { token, user } = useSelector((state: RootState) => state.auth);
  const { loading, chatHistoryLoader } = useSelector(
    (state: RootState) => state.chatbot
  );
  const [isOpen, setIsOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [message, setMessage] = useState("");
  const [ip, setIp] = useState<string>("");
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([
    { sender: "bot", message: "Hi, How can I help you?", properties: [] },
  ]);
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [isUserExist, setIsUserExist] = useState(false);
  const [guestFormData, setGuestFormData] = useState<GuestFormData>({
    name: "",
    email: "",
    mobile: "",
  });
  const chatboxRef = useRef<HTMLDivElement>(null);
  const offset = useRef({ x: 0, y: 0 });
  const hasFetchedChatHistory = useRef(false); // To prevent multiple fetches

  // Fetch IP Address
  useEffect(() => {
    const fetchIp = async () => {
      try {
        const response = await dispatch(getIPAddress());
        setIp(response?.payload?.ip || "unknown");
      } catch (error) {
        console.log("Error while getting IP", error);
        setIp("unknown");
      }
    };
    fetchIp();
  }, [dispatch]);

  // Fetch Guest User Details
  useEffect(() => {
    if (ip && isOpen && !token) {
      fetchGuestUser();
    }
  }, [ip, isOpen, token, dispatch]);

  // Fetch Chat History when conditions are met
  useEffect(() => {
    if (
      isOpen &&
      (token || isUserExist) &&
      !showGuestForm &&
      !hasFetchedChatHistory.current
    ) {
      fetchChatHistory();
      hasFetchedChatHistory.current = true; // Mark as fetched
    }
  }, [
    isOpen,
    token,
    isUserExist,
    showGuestForm,
    dispatch,
    ip,
    user,
    guestFormData,
  ]);

  // Reset chat history fetch flag when chatbox closes
  useEffect(() => {
    if (!isOpen) {
      hasFetchedChatHistory.current = false; // Reset when chatbox closes
      setChatHistory([
        { sender: "bot", message: "Hi, How can I help you?", properties: [] },
      ]);
    }
  }, [isOpen]);

  // Scroll to bottom when chat history updates
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [chatHistory]);

  const fetchGuestUser = async () => {
    try {
      const response = await dispatch(getUserByIp({ ip }));
      if (response?.payload?.Success) {
        setShowGuestForm(false);
        setIsUserExist(true);
        setGuestFormData({
          name: response.payload?.user?.name,
          email: response.payload?.user?.email,
          mobile: response.payload?.user?.mobile,
        });
      } else if (!token) {
        setShowGuestForm(true);
        setIsUserExist(false);
      }
    } catch (error) {
      console.log("error", error);
      if (!token) {
        setShowGuestForm(true);
      }
    }
  };

  const fetchChatHistory = async () => {
    try {
      const data: ChatHistory = {
        IP: token ? null : ip,
        email: user ? user.email : null,
      };
      const response = await dispatch(getChatHistory(data));
      const history = response?.payload?.flatMap((item: ChatItem) => [
        {
          sender: "user",
          message: item.req,
          timestamp: item.timestamp,
          properties: [],
        },
        {
          sender: "bot",
          message: item.res.response,
          timestamp: item.timestamp,
          properties: item.res.properties,
        },
      ]);
      setChatHistory((prev) => [
        {
          sender: "bot",
          message: `Hi ${guestFormData.name || "Guest"}, How can I help you?`,
          properties: [],
        },
        ...(history || []),
      ]);
    } catch (error) {
      console.error("Failed to fetch chat history:", error);
    }
  };

  const toggleChatbox = () => {
    if (!isOpen) {
      if (token) {
        setShowGuestForm(false);
      } else if (!isUserExist) {
        setShowGuestForm(true);
      }
    } else {
      setChatHistory([]); // Clear chat history on close
      if (!token && !isUserExist) setShowGuestForm(true); // Reset to form if signed out and not submitted
    }
    setIsOpen(!isOpen);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (chatboxRef.current) {
      setIsDragging(true);
      const rect = chatboxRef.current.getBoundingClientRect();
      offset.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging && chatboxRef.current) {
      const maxX = window.innerWidth - chatboxRef.current.offsetWidth;
      const maxY = window.innerHeight - chatboxRef.current.offsetHeight;
      let newX = e.clientX - offset.current.x;
      let newY = e.clientY - offset.current.y;
      newX = Math.max(0, Math.min(newX, maxX));
      newY = Math.max(0, Math.min(newY, maxY));
      chatboxRef.current.style.left = `${newX}px`;
      chatboxRef.current.style.top = `${newY}px`;
      chatboxRef.current.style.bottom = "auto";
      chatboxRef.current.style.right = "auto";
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const addEmoji = (emoji: EmojiObject) => {
    setMessage(message + emoji.native);
    setShowEmojiPicker(false);
  };

  const handleSendMessage = async () => {
    if (message.trim() === "") return;

    setChatHistory((prev) => [
      ...prev,
      {
        sender: "user",
        message,
        properties: [],
        timestamp: new Date().toISOString(),
      },
    ]);
    setMessage("");
    try {
      const data: ChatBot = {
        auth: token ? token : null,
        question: message,
        IP: token ? null : ip,
      };
      const response = await dispatch(handleChatBot(data));
      setChatHistory((prev) => [
        ...prev,
        {
          sender: "bot",
          message:
            response?.payload?.response ||
            "Sorry, Please try again with same input.",
          properties: response?.payload?.property_details,
          timestamp: new Date().toISOString(),
        },
      ]);
    } catch (error) {
      console.log("error", error);
      setChatHistory((prev) => [
        ...prev,
        {
          sender: "bot",
          message:
            "Sorry, Please try again with same input after couple of seconds.",
          properties: [],
          timestamp: new Date().toISOString(),
        },
      ]);
    }
  };

  const handleGuestFormSubmit: SubmitHandler<FieldValues> = async (data) => {
    try {
      const payload: GuestFormDataPayload = {
        name: data?.name,
        email: data?.email,
        phone: data?.mobile,
        IP: ip,
      };
      const response = await dispatch(sendChatBotFormData(payload));
      if (response?.payload?.Success) {
        setShowGuestForm(false);
        setIsUserExist(true);
        setGuestFormData({
          name: data.name,
          email: data.email,
          mobile: data.mobile,
        });
        reset();
        setChatHistory([
          {
            sender: "bot",
            message: `Hi ${data.name}, How can I help you?`,
            properties: [],
          },
        ]);
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    } else {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  const handleViewProperty = (name: string) => {
    const formattedName = formatPropertyName(name);
    router.push(`/single-property/${formattedName}`);
  };

  return (
    <div className="relative z-30">
      <button onClick={toggleChatbox} className="fixed bottom-8 right-8">
        <Image src={ai} alt="" />
      </button>

      {isOpen && (
        <div
          ref={chatboxRef}
          className="fixed bottom-20 md:right-5 right-0 md:w-96 w-full bg-white rounded-2xl shadow-lg overflow-hidden"
          style={{ cursor: isDragging ? "grabbing" : "grab" }}
        >
          {chatHistoryLoader && <ChatBotLoader />}
          <div
            className="bg-[#FDE6F4] border-b border-[#F796D0] p-4 rounded-t-2xl flex items-center justify-between"
            onMouseDown={handleMouseDown}
          >
            <div className="flex items-center">
              <Image src={icon} alt="" />
            </div>
            <div className="flex items-center gap-2">
              <button className="cursor-pointer" onClick={toggleChatbox}>
                <Image src={down} alt="" />
              </button>
              <Move className="text-[#201D1F] cursor-grab" />
            </div>
          </div>

          {showGuestForm && !token && !isUserExist ? (
            <div className="p-5 space-y-4">
              <h3 className="text-[#EF2BA0] text-center">
                Please provide your details to start chatting
              </h3>
              <form
                className="space-y-4"
                onSubmit={handleSubmit(handleGuestFormSubmit)}
              >
                <div>
                  <input
                    type="text"
                    {...register("name")}
                    placeholder="Enter your name"
                    className="w-full py-2 px-5 rounded-full border border-[#EF2BA0] outline-none text-black"
                  />
                  {errors.name && (
                    <p className="text-red-500 ml-5 text-sm mt-1">
                      {errors.name.message}
                    </p>
                  )}
                </div>
                <div>
                  <input
                    type="email"
                    {...register("email")}
                    placeholder="Enter your email"
                    className="w-full py-2 px-5 rounded-full border border-[#EF2BA0] outline-none text-black"
                  />
                  {errors.email && (
                    <p className="text-red-500 ml-5 text-sm mt-1">
                      {errors.email.message}
                    </p>
                  )}
                </div>
                <div>
                  <input
                    type="tel"
                    {...register("mobile")}
                    placeholder="Enter your mobile number"
                    className="w-full py-2 px-5 rounded-full border border-[#EF2BA0] outline-none text-black"
                  />
                  {errors.mobile && (
                    <p className="text-red-500 ml-5 text-sm mt-1">
                      {errors.mobile.message}
                    </p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-2 px-5 bg-[#EF2BA0] text-white rounded-full"
                >
                  {isSubmitting ? "Signing in..." : "Submit"}
                </button>
              </form>
            </div>
          ) : (
            (token || isUserExist) && (
              <>
                <div
                  className="p-5 space-y-4 max-h-96 overflow-y-auto custom-scrollbar relative"
                  ref={scrollRef}
                >
                  {chatHistory?.map((chat, index) => (
                    <div
                      key={index}
                      className={`flex flex-col items-start relative ${
                        chat.sender === "user" ? "items-end" : ""
                      }`}
                    >
                      {chat.sender === "bot" && (
                        <Image
                          src="/Boat-icon-chat.png"
                          alt="Bot Avatar"
                          width={30}
                          height={30}
                          className="rounded-full absolute"
                        />
                      )}
                      <div
                        className={`ml-10 mr-10 py-2 px-3 rounded-lg ${
                          chat.sender === "user"
                            ? "bg-[#443F6D] rounded-tr-none"
                            : "bg-[#EF2BA0] rounded-tl-none"
                        } text-white max-w-[70%]`}
                      >
                        {chat.message}
                      </div>
                      {chat.sender === "user" && (
                        <Image
                          src={
                            user &&
                            user.images &&
                            user.images[0] &&
                            user.images[0].path
                              ? `${process.env.NEXT_PUBLIC_API_URL}${user.images[0].path}`
                              : Avatar
                          }
                          alt="User Avatar"
                          width={30}
                          height={30}
                          className="rounded-full object-cover absolute"
                        />
                      )}
                      {chat.sender === "bot" &&
                        chat?.properties?.length > 0 && (
                          <div className="mt-4 space-y-2 w-full">
                            {chat?.properties?.map(
                              (property, propertyIndex) => (
                                <div
                                  key={propertyIndex}
                                  className="flex border p-1 rounded-md bg-white shadow-sm"
                                >
                                  <Image
                                    src={
                                      property?.images?.[0]?.path
                                        ? `${process.env.NEXT_PUBLIC_API_URL}${property.images[0].path}`
                                        : link
                                    }
                                    alt={`Property ${propertyIndex}`}
                                    width={52}
                                    height={39}
                                    className="object-contain"
                                  />
                                  <div className="text-black text-[12px] p-1">
                                    <p>{property?.location?.address}</p>
                                    <p
                                      className="text-[#EF2BA0] cursor-pointer"
                                      onClick={() =>
                                        handleViewProperty(property?.name)
                                      }
                                    >
                                      View Property
                                    </p>
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        )}
                    </div>
                  ))}
                </div>
                {loading && (
                  <div className="flex items-center ml-2 mr-2 py-2 px-3">
                    <div className="dot-pulse">
                      <div></div>
                      <div></div>
                      <div></div>
                    </div>
                  </div>
                )}
                <div className="p-4 bg-white rounded-b-2xl flex items-center relative gap-2">
                  <button
                    className=""
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  >
                    <Image src={smily} alt="" width={30} height={30} />
                  </button>
                  <input
                    type="text"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Enter your message"
                    className="flex-1 py-2 px-5 rounded-full border border-[#EF2BA0] outline-none"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSendMessage();
                      }
                    }}
                  />
                  <button
                    className="absolute right-6 top-1/2 transform -translate-y-1/2"
                    onClick={handleSendMessage}
                  >
                    <Image src={msg} alt="" width={20} height={20} />
                  </button>
                  {showEmojiPicker && (
                    <div className="absolute bottom-full right-0 mb-2 w-full m-auto flex justify-center items-center">
                      <Picker data={data} onEmojiSelect={addEmoji} />
                    </div>
                  )}
                </div>
              </>
            )
          )}
        </div>
      )}
    </div>
  );
}
